import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Row, Col } from 'react-bootstrap';
import { Input, Fieldset, BackLink, Card, Details, Button, Textarea, ErrorSummary } from 'nhsuk-react-components';
import { listEcgs, createEcg, updateEcg, deleteEcg, getEcgDetails } from '../../actions/ecgActions';
import { ECG_CREATE_RESET, ECG_UPDATE_RESET } from '../../constants/ecgConstants';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Callout from '../../components/callout/Callout.component';
import Modal from '../../components/modal/Modal.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

export default function EcgCRUDScreen({ history }) {
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Edit Ecgs";
    }, []);

    useEffect(() => {
        dispatch({ type: ECG_CREATE_RESET });
        dispatch({ type: ECG_UPDATE_RESET });
    }, [dispatch]);

    const [ecgListFilter, setecgListFilter] = useState({ query: '', page: 1, filter: 'Name' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newEcg, setNewEcg] = useState({ name: '', detail: '' });
    const [updatedEcg, setUpdatedEcg] = useState({});

    const ecgList = useSelector((state) => state.ecgList);
    const { loading, error, ecgs, currentPage, totalPages, totalCount } = ecgList;

    const ecgCreate = useSelector((state) => state.ecgCreate);
    const { success: successCreate, error: ecgCreateErrors, loading: ecgCreateLoading } = ecgCreate;

    const ecgUpdate = useSelector((state) => state.ecgUpdate);
    const { success: successUpdate, loading: ecgUpdateProcessing } = ecgUpdate;

    const ecgDetails = useSelector((state) => state.ecgDetails);
    const { ecg: ecgDet, loading: ecgDetailsLoading } = ecgDetails;

    useEffect(() => {
        dispatch(listEcgs(ecgListFilter));
    }, [dispatch, ecgListFilter]);

    useEffect(() => {
        if (ecgDet) {
            setUpdatedEcg({ ...ecgDet });
        }
    }, [ecgDet]);

    useEffect(() => {
        if (successCreate) {
            setShowCreateModal(false);
            setNewEcg({ name: '', detail: '' });
            dispatch(listEcgs(ecgListFilter));
            dispatch({ type: ECG_CREATE_RESET });
        }
    }, [successCreate, dispatch, ecgListFilter]);

    const handlePageChange = (newPage) => {
        setecgListFilter({ ...ecgListFilter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setecgListFilter({ ...ecgListFilter, query: e.target.value, page: 1 });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this ecg?')) {
            dispatch(deleteEcg(id)).then(() => {
                dispatch(listEcgs(ecgListFilter));
                if (updatedEcg?.id === id) {
                    setShowEditModal(false);
                    setUpdatedEcg({});
                }
            });
        }
    };

    const handleEditEcg = (id) => {
        dispatch(getEcgDetails(id));
        setShowCreateModal(false);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setUpdatedEcg({});
        dispatch({ type: ECG_UPDATE_RESET });
    };

    const handleCreateEcg = () => {
        dispatch(createEcg(newEcg));
    };

    const handleUpdateEcg = () => {
        dispatch(updateEcg(updatedEcg.id, updatedEcg)).then(() => {
            dispatch(getEcgDetails(updatedEcg.id));
            dispatch(listEcgs(ecgListFilter));
        });
    };

    const openCreateModal = () => {
        setNewEcg({ name: '', detail: '' });
        dispatch({ type: ECG_CREATE_RESET });
        setShowEditModal(false);
        setShowCreateModal(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md="12">
                            <div className="welcome-box">
                                <PageWrapper title="Ecg Management" totalCount={totalCount} loading={loading} />
                                <BackLink
                                    asElement="a"
                                    href="#"
                                    onClick={() => history.goBack()}
                                >
                                    Return back
                                </BackLink>
                                <Button
                                    className="nhsuk-button--small nhsuk-u-margin-bottom-4"
                                    onClick={openCreateModal}
                                >
                                    + Create Ecg
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <div className="nhsuk-grid-column-one-half">
                        <Card>
                            <Card.Content>
                                <Card.Heading className="nhsuk-heading-m">
                                    <Input
                                        id="search-ecgs"
                                        label="Search ecgs"
                                        name="search-ecgs"
                                        type="search"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchChange(e);
                                            }
                                        }}
                                    />
                                </Card.Heading>
                                <Card.Description>
                                    <Fieldset>
                                        {ecgs && ecgs.length > 0 ? (
                                            ecgs.map((ecg) => (
                                                <Details key={ecg.id || ecg._id}>
                                                    <Details.Summary>
                                                        {ecg.name}
                                                    </Details.Summary>
                                                    <Details.Text>
                                                        <p>
                                                            <strong>Description:</strong> {ecg.detail}
                                                        </p>
                                                        <div className="compact-admin-table__actions">
                                                            <Button
                                                                className="nhsuk-button--small"
                                                                disabled={ecgDetailsLoading}
                                                                onClick={() => handleEditEcg(ecg.id)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                secondary
                                                                className="nhsuk-button--small btn-delete"
                                                                onClick={() => handleDelete(ecg.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Details.Text>
                                                </Details>
                                            ))
                                        ) : null}
                                    </Fieldset>
                                    {loading ? (
                                        <AppSpinner />
                                    ) : error ? (
                                        <p>{error}</p>
                                    ) : (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </Card.Description>
                            </Card.Content>
                        </Card>
                    </div>

                    <div className="nhsuk-grid-column-one-half" />
                </Container>
            </div>

            <Modal
                show={showCreateModal}
                handleClose={() => {
                    setShowCreateModal(false);
                    dispatch({ type: ECG_CREATE_RESET });
                }}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Create Ecg</h2>
                {ecgCreateErrors && (
                    <ErrorSummary aria-labelledby="error-summary-title" role="alert" tabIndex="-1">
                        <ErrorSummary.Title id="error-summary-title">There is a problem</ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {ecgCreateErrors.map((error) => (
                                    <li key={error}>{error}</li>
                                ))}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}
                <Input
                    id="name-create"
                    label="Name"
                    name="name-create"
                    type="text"
                    onChange={(e) => setNewEcg({ ...newEcg, name: e.target.value })}
                    value={newEcg?.name || ''}
                    required
                />
                <Textarea
                    id="detail-create"
                    label="Description"
                    name="detail-create"
                    onChange={(e) => setNewEcg({ ...newEcg, detail: e.target.value })}
                    value={newEcg?.detail || ''}
                />
                <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                    <Button
                        className="nhsuk-button--small"
                        disabled={ecgCreateLoading || ecgUpdateProcessing}
                        onClick={handleCreateEcg}
                    >
                        Create Ecg
                    </Button>
                    <Button
                        secondary
                        className="nhsuk-button--small"
                        onClick={() => {
                            setShowCreateModal(false);
                            dispatch({ type: ECG_CREATE_RESET });
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>

            <Modal
                show={showEditModal}
                handleClose={handleCloseEdit}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Edit Ecg</h2>
                {ecgDetailsLoading ? (
                    <AppSpinner />
                ) : (
                    <>
                        <Input
                            id="name"
                            label="Name"
                            name="name"
                            type="text"
                            onChange={(e) => setUpdatedEcg({ ...updatedEcg, name: e.target.value })}
                            value={updatedEcg.name ? updatedEcg.name : ''}
                            required
                        />
                        <Textarea
                            id="detail"
                            label="Description"
                            name="detail"
                            onChange={(e) => setUpdatedEcg({ ...updatedEcg, detail: e.target.value })}
                            value={updatedEcg.detail ? updatedEcg.detail : ''}
                        />
                        {successUpdate && (
                            <Callout
                                label="Success"
                                header="Success!"
                                autoDismiss={true}
                                dismissTime={5000}
                                time={new Date().toLocaleTimeString()}
                                title="Success!"
                                message="The ecg was updated successfully."
                                mode="success"
                            />
                        )}
                        <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                            <Button
                                className="nhsuk-button--small"
                                disabled={ecgUpdateProcessing}
                                onClick={handleUpdateEcg}
                            >
                                Save changes
                            </Button>
                            <Button
                                secondary
                                className="nhsuk-button--small"
                                onClick={handleCloseEdit}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
}
